import { Button, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components'
import React from 'react'

import { useAuth } from '../hooks/useAuth'

import PageLoading from './PageLoading'
import Unauthorized from './Unauthorized'


interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
}

export default function ProtectedRoute({ children, requiredRoles = [] }: ProtectedRouteProps) {
  const { isAuthenticated, hasAnyRole, roles, loading, error, retry } = useAuth()

  // Show loading spinner while checking authentication
  if (loading) {
    return <PageLoading />
  }

  // User is not authenticated
  if (!isAuthenticated) {
    return <Unauthorized />
  }

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Unable to load your permissions</MessageBarTitle>
          Access is unavailable until your permissions can be loaded. Try again.
        </MessageBarBody>
        <MessageBarActions>
          <Button onClick={retry}>Retry</Button>
        </MessageBarActions>
      </MessageBar>
    )
  }

  // User is authenticated but has no required roles
  if (!hasAnyRole) {
    return <Unauthorized />
  }

  // If specific roles are required, check if user has any of them
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => roles.includes(role))
    if (!hasRequiredRole) {
      return <Unauthorized />
    }
  }

  // User is authorized, render the protected content
  return <>{children}</>
}
